import { FireSQL } from 'firesql';
import firebase from 'firebase/compat/app';
import { useState, useEffect, useCallback } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  limit,
  orderBy,
  startAfter,
  where,
} from "firebase/firestore";
import { create } from "zustand";
import moment from "moment";
import { useParams } from "react-router-dom";

const pageSize = 6;

// define function to detect the string is lower case
function isLowercase(str) {
  return str === str.toLowerCase();
}

export const useVideoStore = create((set) => ({
  videos: [],
  setVideos: (videos) => set({ videos }),
  resetVideos: () => set({ videos: [] }),
}));

export function useVideos() {
  const { dbName } = useParams();
  const [page, setPage] = useState(0);
  const [start, setStart] = useState(null);
  const { videos, setVideos } = useVideoStore();
  const [loading, setLoading] = useState(false);
  const [includePastEvents, setIncludePastEvents] = useState(false);

  const getData = async (startAfterDoc = null, includePastEventsParam = false) => {
    if (!dbName) return;
    if (loading) return;
    setLoading(true);
    setIncludePastEvents(includePastEventsParam); // Store the flag for nextPage
    console.log(`[getData] Called for dbName: '${dbName}', includePastEvents: ${includePastEventsParam}`);
    try {
      const docSnap = await getVideos(startAfterDoc, dbName, includePastEventsParam);
      const newMap = [];
      docSnap.forEach((doc) => newMap.push({ ...doc.data(), id: doc.id }));
      setVideos(newMap);
      if (docSnap.docs.length === pageSize) {
        setStart(docSnap.docs[docSnap.docs.length - 1]);
        console.log(`[getData] Set start to:`, docSnap.docs[docSnap.docs.length - 1]);
      } else {
        setStart(null);
        console.log(`[getData] No more pages.`);
      }
      console.log(`[getData] Loaded videos:`, newMap.length);
    } catch (error) {
      console.error(`[getData] Error for dbName '${dbName}':`, error);
    }
    setLoading(false);
  };

  const getVideoById = async (videoId) => {
    if (!dbName) return null;
    if (!videoId) {
      console.log(`[getVideoById] No videoId provided. dbName: '${dbName}'`);
      return null;
    }
    console.log(`[getVideoById] Attempting to find video for ID: '${videoId}' in dbName: '${dbName}'`);

    const existingVideo = videos.find(v =>
      v.url === videoId ||
      v.id === videoId
    );

    if (existingVideo) {
      console.log(`[getVideoById] Found video in existing client-side list for ID: '${videoId}'`, existingVideo);
      return existingVideo;
    }
    console.log(`[getVideoById] Video ID '${videoId}' not found in client-side list. Querying Firestore for dbName: '${dbName}'.`);

    try {
      const videoRef = collection(db, dbName);
      let foundVideoData = null;
      let queryDescription = "";

      // --- Primary Query ---
      queryDescription = `field 'url' == '${videoId}'`;
      console.log(`[getVideoById] Firestore Query (1): ${queryDescription} in db '${dbName}'`);
      const urlExactQuery = query(videoRef, where("url", "==", videoId), limit(1));
      const urlExactSnapshot = await getDocs(urlExactQuery);

      if (!urlExactSnapshot.empty) {
        foundVideoData = { ...urlExactSnapshot.docs[0].data(), id: urlExactSnapshot.docs[0].id };
        console.log(`[getVideoById] SUCCESS (Query 1): Found video using ${queryDescription}`, foundVideoData);
      } else {
        console.log(`[getVideoById] FAILED (Query 1): No video found using ${queryDescription}`);

        // --- Secondary Query ---
        queryDescription = `field 'id' == '${videoId}'`;
        console.log(`[getVideoById] Firestore Query (2): ${queryDescription} in db '${dbName}'`);
        const idExactQuery = query(videoRef, where("id", "==", videoId), limit(1));
        const idExactSnapshot = await getDocs(idExactQuery);
        if (!idExactSnapshot.empty) {
          foundVideoData = { ...idExactSnapshot.docs[0].data(), id: idExactSnapshot.docs[0].id };
          console.log(`[getVideoById] SUCCESS (Query 2): Found video using ${queryDescription}`, foundVideoData);
        } else {
          console.log(`[getVideoById] FAILED (Query 2): No video found using ${queryDescription}`);
          // --- Tertiary Query ---
          const fullYoutubeUrl = `https://www.youtube.com/watch?v=${videoId}`;
          queryDescription = `field 'url' == '${fullYoutubeUrl}'`;
          console.log(`[getVideoById] Firestore Query (3): ${queryDescription} in db '${dbName}'`);
          const urlFullQuery = query(videoRef, where("url", "==", fullYoutubeUrl), limit(1));
          const urlFullSnapshot = await getDocs(urlFullQuery);
          if (!urlFullSnapshot.empty) {
            foundVideoData = { ...urlFullSnapshot.docs[0].data(), id: urlFullSnapshot.docs[0].id };
            console.log(`[getVideoById] SUCCESS (Query 3): Found video using ${queryDescription}`, foundVideoData);
          } else {
            console.log(`[getVideoById] FAILED (Query 3): No video found using ${queryDescription}`);
          }
        }
      }

      if (foundVideoData) {
        console.log(`[getVideoById] Video found for '${videoId}'. Proceeding to load context videos for db '${dbName}'.`);
        const timestamp = foundVideoData.timestamp || 0;
        const daysBeforeAfter = 5;
        const startTime = timestamp - (daysBeforeAfter * 86400);
        const endTime = timestamp + (daysBeforeAfter * 86400);

        console.log(`[getVideoById] Getting context videos between: ${new Date(startTime * 1000).toLocaleDateString()} and ${new Date(endTime * 1000).toLocaleDateString()}`);

        const contextQuery = query(
          collection(db, dbName),
          where('timestamp', '>=', startTime),
          where('timestamp', '<=', endTime),
          orderBy('timestamp'),
          limit(30)
        );
        const contextSnapshot = await getDocs(contextQuery);
        let contextVideos = contextSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
        const isTargetVideoInContext = contextVideos.some(v => v.id === foundVideoData.id);
        if (!isTargetVideoInContext) {
          contextVideos.unshift(foundVideoData);
        }
        contextVideos.sort((a, b) => a.timestamp - b.timestamp);
        setVideos(contextVideos);
        return foundVideoData;
      }
      console.log(`[getVideoById] All Firestore query attempts FAILED for video ID: '${videoId}' in dbName: '${dbName}'`);
      return null;
    } catch (error) {
      console.error(`[getVideoById] CRITICAL ERROR for video ID: '${videoId}', dbName: '${dbName}':`, error);
      return null;
    }
  };

  useEffect(() => {
    if (videos.length === 0) {
      if (!dbName) return;
      console.log(`[useEffect in useVideos] videos array is empty, calling getData for dbName: '${dbName}'`);
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos.length, dbName]);

  const nextPage = useCallback(
    async function nextPage() {
      if (!dbName) return;
      if (loading || !start) {
        console.log(`[nextPage] Skipped. Loading: ${loading}, Start:`, start);
        return;
      }
      setLoading(true);
      console.log(`[nextPage] Called for dbName: '${dbName}' with start:`, start, `includePastEvents: ${includePastEvents}`);
      try {
        // Use the same includePastEvents flag as the initial load to maintain query consistency
        const docSnap = await getVideos(start, dbName, includePastEvents);
        const newMap = [...videos];
        docSnap.forEach((doc) => newMap.push({ ...doc.data(), id: doc.id }));
        setVideos(newMap);
        if (docSnap.docs.length === pageSize) {
          setStart(docSnap.docs[docSnap.docs.length - 1]);
          console.log(`[nextPage] Set start to:`, docSnap.docs[docSnap.docs.length - 1]);
        } else {
          setStart(null);
          console.log(`[nextPage] No more pages.`);
        }
        console.log(`[nextPage] Loaded videos:`, newMap.length);
      } catch (error) {
        console.error(`[nextPage] Error for dbName '${dbName}':`, error);
      }
      setLoading(false);
    },
    [start, loading, videos, setVideos, dbName, includePastEvents]
  );

  useEffect(() => {
    if (videos.length <= 3) return;
    if (page > videos.length - 3) {
      nextPage();
    }
  }, [page, videos, nextPage]);

  function convertToTitleCase(word) {
    if (!word) return '';
    const words = word.split(' ');
    const titleCaseWords = words.map(word => word.charAt(0).toUpperCase() + word.slice(1));
    return titleCaseWords.join(' ');
  }

  // Helper to escape SQL single quotes for FireSQL queries
  function escapeSQL(str) {
    if (!str) return '';
    return str.replace(/'/g, "''");
  }


  async function getBandVideos(bandName, venueName, location, startPrice, endPrice, startDate, endDate, ticketURL) {
     // make first letter upper case
    const dbRef = firebase.firestore();
    const fireSQL = new FireSQL(dbRef);

    // convert words to lower case and first letter to upper case
    let bandNameConverted = convertToTitleCase(bandName);
    let venueNameConverted = convertToTitleCase(venueName);
    //let locationConverted = convertToTitleCase(location);
    let locationConverted = location;
    const startTimeStamp = moment(startDate).unix();
    // end date is 2 years after start date
    const endTimeStamp = moment(startDate).add(2, 'years').unix();
    //const endTimeStamp = endDate ? moment(endDate).unix() : null;

    //annoying firebase can only query on one field at a time so have to do 2 queries

    // Build band query with case variations and "The" prefix support
    let bandQuery = "";
    if (bandNameConverted) {
      const escapedBand = escapeSQL(bandNameConverted);
      const escapedBandLower = escapedBand.toLowerCase();
      const escapedBandUpper = escapedBand.toUpperCase();
      // Query with multiple case patterns and support "The" prefix
      bandQuery = `SELECT * FROM ${dbName} WHERE (
        band LIKE '${escapedBand}%' OR band LIKE 'The ${escapedBand}%' OR
        band LIKE '${escapedBandLower}%' OR band LIKE 'the ${escapedBandLower}%' OR
        band LIKE '${escapedBandUpper}%' OR band LIKE 'THE ${escapedBandUpper}%'
      )`;
    }

    // Build venue query - FireSQL only supports "value%" (starts with) pattern
    // Query broadly and filter client-side to find venues containing the term
    let venueQuery = "";
    if (venueName && venueName.trim()) {
      const escapedVenue = escapeSQL(venueNameConverted);
      const escapedVenueLower = escapedVenue.toLowerCase();
      const escapedVenueUpper = escapedVenue.toUpperCase();
      // Query for venues that START with the term (FireSQL limitation)
      // We'll filter client-side to find venues containing the term anywhere
      venueQuery = `SELECT * FROM ${dbName} WHERE (
        venue LIKE '${escapedVenue}%' OR 
        venue LIKE '${escapedVenueLower}%' OR 
        venue LIKE '${escapedVenueUpper}%'
      )`;
      console.log('Venue query:', venueQuery);
    }

    // Location query - keep exact match as towns are standardized
    let locationQuery = locationConverted ? `SELECT * FROM ${dbName} WHERE town = '${escapeSQL(locationConverted)}'` : "";
    let dateQuery = startDate && endDate ? "SELECT * FROM " + dbName + " WHERE timestamp BETWEEN " + startTimeStamp + " AND " + endTimeStamp : "";
    console.log("startDate = "+startDate + " endDate = "+endDate)


    let bandDoc = []
    let venueDoc = [];
    let locDoc = [];
    let dateDoc = [];
    let intersection = []

    // Execute all queries in parallel for better performance
    const queryPromises = [];
    if (bandName) {
      queryPromises.push(
        fireSQL.query(bandQuery).then(documents => {
          // Additional client-side case-insensitive filtering for safety
          const searchTermLower = bandNameConverted.toLowerCase();
          documents.forEach((doc) => {
            const bandName_lower = (doc.band || '').toLowerCase();
            // Match if band starts with search term OR "the " + search term
            if (bandName_lower.startsWith(searchTermLower) || 
                bandName_lower.startsWith('the ' + searchTermLower)) {
              bandDoc.push({...doc, id: doc.id});
            }
          });
        }).catch(err => {
          console.error('Band query error:', err);
        })
      );
    }

    if (venueName) {
      queryPromises.push(
        fireSQL.query(venueQuery).then(documents => {
          console.log(`Venue query returned ${documents.length} documents`);
          // Filter for case-insensitive matching - match if term appears anywhere
          // This handles cases where the venue is stored in different case
          const searchTermLower = (venueName || '').toLowerCase();
          documents.forEach((doc) => {
            const venueName_lower = (doc.venue || '').toLowerCase();
            // Match if venue contains the search term anywhere (case-insensitive)
            // This will match "Bristol Strange Brew" when searching for "strange"
            if (venueName_lower.includes(searchTermLower)) {
              venueDoc.push({...doc, id: doc.id});
            }
          });
          console.log(`After filtering, venueDoc has ${venueDoc.length} documents`);
        }).catch(err => {
          console.error('Venue query error:', err);
          console.error('Venue query was:', venueQuery);
        })
      );
    }

    if (location) {
      queryPromises.push(
        fireSQL.query(locationQuery).then(documents => {
          documents.forEach((doc) => locDoc.push({...doc, id: doc.id}));
        }).catch(err => {
          console.error('Location query error:', err);
        })
      );
    }

    if (startDate && endDate) {
      queryPromises.push(
        fireSQL.query(dateQuery).then(documents => {
          documents.forEach((doc) => dateDoc.push({...doc, id: doc.id}));
        }).catch(err => {
          console.error('Date query error:', err);
        })
      );
    }

    // Wait for all queries to complete in parallel
    await Promise.all(queryPromises);

    //console.log("lengths = "+bandDoc.length + " " + venueDoc.length + " " + locDoc.length + " " + dateDoc.length + "")

    //get intersection of bandDoc/venueDoc and locDoc and dateDoc
    //horribly contrived since can't do proper sql in firebase!!!
    // Optimize intersection by using Sets for O(1) lookups instead of O(n) .some() calls
    const createUrlSet = (docs) => new Set(docs.map(doc => doc.url));
    
    // Determine which sets we need for intersection
    const locUrlSet = location ? createUrlSet(locDoc) : null;
    const dateUrlSet = (startDate && endDate) ? createUrlSet(dateDoc) : null;
    
    // Helper function to check if a document matches all required filters
    const matchesFilters = (doc) => {
      if (locUrlSet && !locUrlSet.has(doc.url)) return false;
      if (dateUrlSet && !dateUrlSet.has(doc.url)) return false;
      return true;
    };
    
    // Build intersection based on search parameters
    if (venueName && location && startDate && endDate) {
      intersection = venueDoc.filter(matchesFilters);
    } else if (venueName && location) {
      intersection = venueDoc.filter(doc => locUrlSet.has(doc.url));
    } else if (venueName && startDate && endDate) {
      intersection = venueDoc.filter(doc => dateUrlSet.has(doc.url));
    } else if (bandName && location && startDate && endDate) {
      intersection = bandDoc.filter(matchesFilters);
    } else if (bandName && location) {
      intersection = bandDoc.filter(doc => locUrlSet.has(doc.url));
    } else if (bandName && startDate && endDate) {
      intersection = bandDoc.filter(doc => dateUrlSet.has(doc.url));
    } else if (venueName) {
      intersection = venueDoc;
    } else if (location && startDate && endDate) {
      intersection = locDoc.filter(doc => dateUrlSet.has(doc.url));
    } else if (bandName) {
      intersection = bandDoc;
    } else if (location) {
      intersection = locDoc;
    } else if (startDate && endDate) {
      intersection = dateDoc;
    }
  
    //console.log("intersection = "+JSON.stringify(intersection));

    // Sort results by date (timestamp) - ascending order (earliest first)
    intersection.sort((a, b) => {
      const timestampA = a.timestamp || 0;
      const timestampB = b.timestamp || 0;
      return timestampA - timestampB;
    });

    return intersection;
  }


  const searchVideo = async (props) => {
    let res = []
    const { location, bandName, venueName, startPrice, endPrice, startDate, endDate, setTotalSizeCallback } =  props;
    console.log("searchVideo query - bandName:", bandName, "venueName:", venueName)
    
    // Clear existing videos immediately to prevent showing old content
    setVideos([]);
    setLoading(true);
    
    res = await getBandVideos(bandName, venueName, location, startPrice, endPrice, startDate, endDate);
    console.log("searchVideo results count:", res.length, "first result:", res.length > 0 ? res[0]?.band : 'none');

    if (res.length === 0) {
      alert("No results found");
      setLoading(false);
      return;
    }

    if (setTotalSizeCallback) {
      setTotalSizeCallback(res.length);
    }

    // Reset page before setting videos to ensure correct display
    setPage(0);
    // Set new videos - this should trigger re-render
    setVideos(res);
    setLoading(false);
  };

  return {
    videos,
    page,
    setPage,
    getData,
    searchVideo,
    getVideoById,
    loading,
    nextPage
  }; // also could return nextPage and searchForBand
}

export async function getVideos(startAfterDoc, dbName, includePastEvents = false) { // Added includePastEvents parameter
  if (!dbName) {
    console.error("[getVideos] dbName is undefined or null!");
    // Return an empty snapshot-like object to prevent errors downstream
    return { docs: [], empty: true, forEach: () => {} };
  }
  const colRef = collection(db, dbName);
  const timeNow = new Date().getTime()/1000;
  const sortBy = "band"; // This might need to be dynamic based on dbName type

  let q;
  if (!isLowercase(dbName)) {
    console.log(`[getVideos] Querying mixed-case db: '${dbName}', orderBy: '${sortBy}', pageSize: ${pageSize}, startAfterDoc:`, startAfterDoc);
    if (startAfterDoc) {
      q = query(colRef, orderBy(sortBy), startAfter(startAfterDoc), limit(pageSize));
    } else {
      q = query(colRef, orderBy(sortBy), limit(pageSize));
    }
  } else {
    // For shared links, includePastEvents=true means we should load all events (past and future)
    // to match the position calculation. For normal browsing, we only show future events.
    if (includePastEvents) {
      console.log(`[getVideos] Querying lowercase db: '${dbName}', ALL events (shared link), orderBy: 'timestamp', pageSize: ${pageSize}, startAfterDoc:`, startAfterDoc);
      if (startAfterDoc) {
        q = query(colRef, orderBy("timestamp"), startAfter(startAfterDoc), limit(pageSize));
      } else {
        q = query(colRef, orderBy("timestamp"), limit(pageSize));
      }
    } else {
      console.log(`[getVideos] Querying lowercase db: '${dbName}', future events, orderBy: 'timestamp', pageSize: ${pageSize}, startAfterDoc:`, startAfterDoc);
      if (startAfterDoc) {
        q = query(colRef, where("timestamp", ">=", timeNow), orderBy("timestamp"), startAfter(startAfterDoc), limit(pageSize));
      } else {
        q = query(colRef, where("timestamp", ">=", timeNow), orderBy("timestamp"), limit(pageSize));
      }
    }
  }
  try {
    const snap = await getDocs(q);
    console.log(`[getVideos] Query returned ${snap.docs.length} docs.`);
    return snap;
  } catch (error) {
    console.error(`[getVideos] Error querying collection '${dbName}':`, error);
    return { docs: [], empty: true, forEach: () => {} }; // Return empty on error
  }
}

/**
 * Given a videoId, find the page number (zero-based) and index within the page for that event.
 * Returns { pageNumber, indexInPage, event } or null if not found.
 */
export async function findEventPageForVideoId(videoId, dbName, pageSize = 6) {
  if (!dbName || !videoId) return null;
  const colRef = collection(db, dbName);

  // 1. Find the event by videoId (try url, id, and full YouTube URL)
  let event = null;
  let eventSnapshot = await getDocs(query(colRef, where("url", "==", videoId), limit(1)));
  if (!eventSnapshot.empty) {
    event = { ...eventSnapshot.docs[0].data(), id: eventSnapshot.docs[0].id };
  }
  if (!event) {
    eventSnapshot = await getDocs(query(colRef, where("id", "==", videoId), limit(1)));
    if (!eventSnapshot.empty) {
      event = { ...eventSnapshot.docs[0].data(), id: eventSnapshot.docs[0].id };
    }
  }
  if (!event) {
    const fullYoutubeUrl = `https://www.youtube.com/watch?v=${videoId}`;
    eventSnapshot = await getDocs(query(colRef, where("url", "==", fullYoutubeUrl), limit(1)));
    if (!eventSnapshot.empty) {
      event = { ...eventSnapshot.docs[0].data(), id: eventSnapshot.docs[0].id };
    }
  }
  if (!event) return null;

  // 2. Count how many events are before this one
  let count = 0;
  let sorted = [];
  if (!isLowercase(dbName)) {
    // Mixed case: order by band
    const q = query(colRef, orderBy("band"));
    const allDocs = await getDocs(q);
    sorted = allDocs.docs.map(doc => ({ ...doc.data(), id: doc.id }));
    sorted.sort((a, b) => (a.band || "").localeCompare(b.band || ""));
    count = sorted.findIndex(v => v.id === event.id);
  } else {
    // Lowercase: order by timestamp
    // For shared links, we need to calculate position using ALL events (past and future)
    // to ensure the link remains stable over time. If we only used future events,
    // past events would disappear from the position calculation.
    const q = query(colRef, orderBy("timestamp"));
    const allDocs = await getDocs(q);
    sorted = allDocs.docs.map(doc => ({ ...doc.data(), id: doc.id }));
    sorted.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
    count = sorted.findIndex(v => v.id === event.id);
  }
  // Debug logging
  console.log('[findEventPageForVideoId] videoId:', videoId);
  console.log('[findEventPageForVideoId] event:', event);
  console.log('[findEventPageForVideoId] sorted ids:', sorted.map(v => v.id));
  console.log('[findEventPageForVideoId] count (index):', count);
  if (count === -1) return null;
  const pageNumber = Math.floor(count / pageSize);
  const indexInPage = count % pageSize;
  return { pageNumber, indexInPage, event, count };
}

export async function getStartAfterDocForPage(dbName, pageNumber, pageSize) {
  if (pageNumber === 0) return null;
  const colRef = collection(db, dbName);
  let q;
  if (!isLowercase(dbName)) {
    q = query(colRef, orderBy("band"), limit(pageNumber * pageSize));
  } else {
    // For shared links, we need to use ALL events (past and future) to match
    // the position calculation in findEventPageForVideoId. This ensures
    // the page number calculated matches the actual page we're loading.
    q = query(colRef, orderBy("timestamp"), limit(pageNumber * pageSize));
  }
  const snap = await getDocs(q);
  if (snap.docs.length === 0) return null;
  return snap.docs[snap.docs.length - 1];
}